import { useContext, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';
import { Context } from '../../AuthContext';
import LinkItem from '../../pages/Workbench/components/LinkItem';
import WatchGuideLink from '../WatchGuideLink/WatchGuideLink';
import {
  faviconUrl,
  validateUrl,
  invalidUrlError,
  generateId,
} from '../../utils/functions';
import {
  openGoogleDrivePicker,
  GoogleDriveNotConnectedError,
} from '../../utils/googlePicker';

const editUrlSuffix = (category, cardHeader) => {
  if (category === 'people') {
    if (cardHeader === 'AUTHOR') return 'author';
    if (cardHeader === 'INVENTOR') return 'inventor';
    if (cardHeader === 'CREATOR' || cardHeader === 'EDITOR' || cardHeader === 'MEMBER') {
      return 'user_by_id';
    }
    return 'person';
  }
  return category.slice(0, -1);
};

const LinkFilesSection = ({
  category,
  cardHeader,
  _id,
  verticalId,
  files: filesProp,
  canEdit,
  onChange,
}) => {
  const { user } = useContext(Context);
  const [files, setFiles] = useState(filesProp || []);
  const [expanded, setExpanded] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');
  const [importError, setImportError] = useState('');

  const persist = async (nextFiles) => {
    setFiles(nextFiles);
    onChange?.(nextFiles);
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const suffix = editUrlSuffix(category, cardHeader);
      const url = `${process.env.REACT_APP_FLASK_WEBSERVER}edit_${suffix}?id=${_id}&vertical_id=${verticalId}`;
      await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ linked_files: nextFiles }),
      });
    } catch (err) {
      console.error('Failed to save linked files:', err);
    }
  };

  const openEditDialog = (file) => {
    setEditingFile(file);
    setTitle(file.title || '');
    setLink(file.link || '');
    setImportError('');
    setDialogOpen(true);
  };

  const handleDelete = (file) => {
    persist(files.filter((f) => f._id !== file._id));
  };

  const handleSaveEdit = (e) => {
    e.preventDefault();
    if (!editingFile) return;
    const trimmedTitle = (title || '').trim() || 'Untitled';
    const trimmedLink = (link || '').trim();
    if (!trimmedLink) return;
    const next = files.map((f) =>
      f._id === editingFile._id ? { ...f, title: trimmedTitle, link: trimmedLink } : f
    );
    persist(next);
    setDialogOpen(false);
  };

  const addFile = (name, pickedLink) => {
    if (!pickedLink) return;
    const next = [
      ...files,
      {
        _id: generateId(),
        title: name || 'Untitled',
        link: pickedLink,
        created_at: new Date().toISOString(),
      },
    ];
    persist(next);
  };

  const addFromGoogleDrive = async () => {
    setImportError('');
    try {
      await openGoogleDrivePicker({
        user,
        onPick: ({ link: pickedLink, name }) => addFile(name, pickedLink),
      });
    } catch (err) {
      if (err instanceof GoogleDriveNotConnectedError) {
        setImportError('not_connected');
      } else {
        console.error('Google Picker error:', err);
        setImportError('generic');
      }
    }
  };

  const addFromDropbox = () => {
    setImportError('');
    if (!window.Dropbox) {
      console.error('Dropbox SDK not loaded');
      setImportError('generic');
      return;
    }
    window.Dropbox.choose({
      success: (picked) => {
        const f = picked && picked[0];
        if (!f) return;
        addFile(f.name, f.link);
      },
      linkType: 'preview',
      multiselect: false,
    });
  };

  const count = files.length;
  const stop = (e) => e.stopPropagation();

  return (
    <div
      onClick={stop}
      style={{
        border: '1px solid #DDDEDD',
        borderRadius: '4px',
        backgroundColor: '#F9F9FC',
        paddingLeft: '15px',
        paddingRight: '15px',
        marginTop: '10px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '12px',
          marginBottom: '12px',
        }}
      >
        <div>
          <p
            style={{
              fontWeight: 600,
              lineHeight: '20px',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            Link Files
            {canEdit && <WatchGuideLink videoId="cloud-storage-file-attachments" />}
          </p>
          <span style={{ fontWeight: 400, fontSize: '14px' }}>
            {count} {count === 1 ? 'File' : 'Files'} Linked
            {count > 0 && (
              <>
                {' ('}
                <span
                  onClick={() => setExpanded((v) => !v)}
                  style={{ color: '#356AC3', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {expanded ? 'Collapse' : 'Expand'}
                </span>
                {')'}
              </>
            )}
          </span>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <IconButton
              size="small"
              title="Import from Google Drive"
              onClick={addFromGoogleDrive}
            >
              <img
                src="/workbench/google-drive.svg"
                alt="Google Drive"
                style={{ width: 22, height: 22 }}
              />
            </IconButton>
            <IconButton
              size="small"
              title="Import from Dropbox"
              onClick={addFromDropbox}
            >
              <img
                src="/workbench/dropbox.svg"
                alt="Dropbox"
                style={{ width: 22, height: 22 }}
              />
            </IconButton>
          </div>
        )}
      </div>

      {importError === 'not_connected' && (
        <div style={{ color: '#B42318', fontSize: 12, marginBottom: 8 }}>
          Connect Google Drive in{' '}
          <RouterLink to="/profile/connected-accounts" style={{ color: '#356AC3' }}>
            Account → My Profile → Connected Accounts
          </RouterLink>
          .
        </div>
      )}
      {importError === 'generic' && (
        <div style={{ color: '#B42318', fontSize: 12, marginBottom: 8 }}>
          Could not open cloud picker. Please try again.
        </div>
      )}

      {expanded && count > 0 && (
        <div style={{ paddingBottom: '12px' }}>
          {files.map((file) => (
            <LinkItem
              key={file._id}
              title={file.title}
              link={file.link}
              icon={faviconUrl(file.link)}
              deleteHandler={() => handleDelete(file)}
              editHandler={() => openEditDialog(file)}
              canEdit={canEdit}
            />
          ))}
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onClick={stop}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontSize: '26px', fontWeight: 700, textAlign: 'left' }}>
          Edit Link
          <IconButton
            aria-label="close"
            onClick={() => setDialogOpen(false)}
            sx={{ position: 'absolute', right: 18, top: 18, color: '#000' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ marginTop: 10 }}>
          <form onSubmit={handleSaveEdit}>
            <input
              type="text"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input-field"
              style={{
                border: '1px solid #98A2B3',
                width: '100%',
                padding: '10px',
                borderRadius: '5px',
                marginBottom: '10px',
                color: '#000',
                outline: 'none',
              }}
              required
            />
            <input
              type="text"
              placeholder="Link"
              pattern={validateUrl}
              title={invalidUrlError}
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="input-field"
              style={{
                border: '1px solid #98A2B3',
                width: '100%',
                padding: '10px',
                borderRadius: '5px',
                marginBottom: '10px',
                color: '#000',
              }}
              required
            />
            <DialogActions sx={{ paddingX: 0 }}>
              <Button
                type="button"
                onClick={() => setDialogOpen(false)}
                style={{
                  padding: '6px 18px',
                  borderRadius: '5px',
                  fontSize: '16px',
                  border: '1px solid #356AC3',
                  textTransform: 'inherit',
                  color: '#356AC3',
                  fontWeight: 550,
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                style={{
                  backgroundColor: '#356AC3',
                  color: '#FFF',
                  padding: '8px 24px',
                  textTransform: 'inherit',
                  borderRadius: '5px',
                  fontSize: '16px',
                  border: 0,
                  fontWeight: 550,
                }}
              >
                Save
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LinkFilesSection;
